import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { PaginationUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { ApiAction, MiddlewareApiAction, createApiAction } from 'api'
import { userLoggedOut } from 'core/session/slices/session'
import {
  getEventDetails,
  bookEvent,
  cancelBooking,
  joinWaitlist,
  leaveWaitlist,
} from 'modules/virtualClassrooms/student/EventDetails/api/eventDetailsService'
import { ScheduleItem } from 'modules/virtualClassrooms/student/EventDetails/types'
import { EventDetailsState, ApiEventDetailsResponse, EventActionRequestParams } from './types'

const initialState: EventDetailsState = {
  eventDetailsData: {},
  bookEventRequest: {},
  cancelBookingRequest: {},
  joinWaitlistRequest: {},
  leaveWaitlistRequest: {},
}

const eventDetailsSlice = createSlice({
  name: 'virtualClassroomStudentEventDetails',
  initialState,
  reducers: {
    eventDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiEventDetailsResponse>>
    ) {
      state.eventDetailsData = action.payload
    },
    clearEventDetails(state) {
      state.eventDetailsData = initialState.eventDetailsData
    },
    joinWaitlistRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.joinWaitlistRequest = action.payload
    },
    leaveWaitlistRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.leaveWaitlistRequest = action.payload
    },
    bookEventRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.bookEventRequest = action.payload
    },
    cancelBookingRequested(state, action: PayloadAction<ApiMiddlewarePayload>) {
      state.cancelBookingRequest = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  eventDetailsLoaded,
  clearEventDetails,
  joinWaitlistRequested,
  bookEventRequested,
  cancelBookingRequested,
  leaveWaitlistRequested,
} = eventDetailsSlice.actions

export { clearEventDetails }
export default eventDetailsSlice.reducer

export const getSchedulesPageItems = createSelector(
  (schedules: ScheduleItem[] | undefined, currentPageId: number, currentPageSize: number) => ({
    schedules,
    currentPageId,
    currentPageSize,
  }),
  ({ schedules, currentPageId, currentPageSize }) =>
    PaginationUtils.getPageItems<ScheduleItem>(schedules, currentPageId, currentPageSize)
)

export const loadEventDetails = (eventId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      eventDetails: () => getEventDetails(eventId),
    },
    payloadAction: eventDetailsLoaded,
    getResultToKeep: state => state.virtualClassroomStudentEventDetails.eventDetailsData.result,
  })
}

const handleEventActionRequest =
  (response: ApiMiddlewarePayload, params: EventActionRequestParams): AppThunk =>
  async dispatch => {
    const { eventId, payloadAction } = params
    dispatch(payloadAction(response))
    if (!response.error) dispatch(loadEventDetails(eventId) as MiddlewareApiAction)
  }

export const joinWaitlistRequest = (eventId: Guid, scheduleId?: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => joinWaitlist(eventId, scheduleId),
    },
    payloadAction: joinWaitlistRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    nextAction: {
      action: handleEventActionRequest,
      params: { eventId, payloadAction: joinWaitlistRequested },
    },
  })
}

export const leaveWaitlistRequest = (eventId: Guid, scheduleId?: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => leaveWaitlist(eventId, scheduleId),
    },
    payloadAction: leaveWaitlistRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    nextAction: {
      action: handleEventActionRequest,
      params: { eventId, payloadAction: leaveWaitlistRequested },
    },
  })
}

export const bookEventRequest = (eventId: Guid, scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => bookEvent(scheduleId),
    },
    payloadAction: bookEventRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    nextAction: {
      action: handleEventActionRequest,
      params: { eventId, payloadAction: bookEventRequested },
    },
  })
}

export const cancelBookingRequest = (eventId: Guid, scheduleId: Guid): ApiAction => {
  return createApiAction({
    apiCalls: {
      request: () => cancelBooking(scheduleId),
    },
    payloadAction: cancelBookingRequested,
    enableErrorNotification: true,
    shouldShowGlobalLoading: true,
    nextAction: {
      action: handleEventActionRequest,
      params: { eventId, payloadAction: cancelBookingRequested },
    },
  })
}
